/**
 * 告知・メンテナンス `/announcements`（R66 / A147 / A174 / A127）。
 *
 * 契約者へ「事前に伝える」経路をまとめた画面。
 *
 * **`maintenance_windows.announced_at` は列としては在ったのに、立てても
 * 誰にも通知されなかった。** SLA の免責条件は「事前に告知した計画メンテナンス」
 * なので、通知の実体が無いまま除外に使うのは掲示と食い違う。ここから
 * `POST /ops/maintenance-windows/:id/announce` を撃つと、影響組織
 * （ノード指定が無ければ全契約者）へアプリ内通知とメールが実際に飛び、
 * **配れたときだけ** `announcedAt` が立つ。
 *
 * 稼働率の算定から除外できるのは `excludeFromSla` が真のものだけで、これは
 * **登録時に「掲示の日数（既定 7 日）以上前に告知したか」で機械的に決まる**。
 * 後から告知しても遡って除外にはならない（後出しの免責を作らないため）。
 * 一覧では、その判定結果をバッジで必ず見せる。
 *
 * 障害の公開文面（`incidents.public_message`）の作成と配信（「契約者へ告知する」）は
 * `/incidents` 側にあるので、ここでは「文面が無いまま進行中の障害」を
 * 検出して導線を出すだけにする（同じ操作を 2 か所に置かない）。
 * **この画面と `/incidents` が互いを指すだけで、どちらにも配信ボタンが
 * 無かった時期がある。** 導線を動かすときは、必ず片方に実物があることを
 * 確かめること。
 */
import * as React from 'react';
import { Link, getRouteApi } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarClock, Megaphone, Plus, Siren } from 'lucide-react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  DataTable,
  Input,
  PageHeader,
  Section,
  StatusBadge,
  Textarea,
  formatDateTime,
  formatRelative,
  type DataTableColumn,
} from '@mail-sv/ui';
import { api } from '../lib/api.js';
import { qk } from '../lib/query-keys.js';
import { OPS_PERMISSION } from '../lib/permissions.js';
import {
  INCIDENT_SEVERITY_LABEL,
  INCIDENT_SEVERITY_TONE,
  INCIDENT_STATUS_LABEL,
  MAINTENANCE_KIND_LABEL,
  MAINTENANCE_KIND_TONE,
  labelFrom,
  optionsFromLabels,
  toneFrom,
} from '../lib/ops-labels.js';
import { usePermission } from '../stores/auth.js';
import { ConfirmTextDialog, FormDialog } from '../components/dialogs.js';
import { Field, NativeSelect } from '../components/form-fields.js';
import { PageTabs } from '../components/page-tabs.js';
import { ListEmpty, PermissionDenied, QueryError, RefetchingDot } from '../components/states.js';
import { RID } from './ids.js';
import type {
  AnnouncementsResponse,
  MaintenanceWindowEntry,
  MaintenanceWindowPage,
  NodeSummary,
} from '../types/api.js';

const routeApi = getRouteApi(RID.announcements);

export function AnnouncementsPage(): React.ReactElement {
  const search = routeApi.useSearch();
  const navigate = routeApi.useNavigate();
  const canReadSecurity = usePermission(OPS_PERMISSION.securityRead);
  const canReadBilling = usePermission(OPS_PERMISSION.billingRead);

  return (
    <>
      <PageHeader
        title="告知・メンテナンス"
        description="契約者へ事前に伝えるものの一覧。計画メンテナンスは、告知して初めて稼働率の算定から除外できます。"
      />

      <PageTabs
        tabs={[
          { value: 'current' as const, label: '進行中・予定', icon: Megaphone, visible: canReadSecurity },
          { value: 'windows' as const, label: 'メンテナンス予定', icon: CalendarClock, visible: canReadBilling },
        ]}
        value={search.tab}
        onChange={(tab) => void navigate({ search: (prev) => ({ ...prev, tab }) })}
      />

      {search.tab === 'current' ? canReadSecurity ? <CurrentTab /> : <PermissionDenied /> : null}
      {search.tab === 'windows' ? canReadBilling ? <WindowsTab /> : <PermissionDenied /> : null}
    </>
  );
}

/* ================================================== 進行中・予定の告知 */

function CurrentTab(): React.ReactElement {
  const query = useQuery({
    queryKey: qk.announcements(),
    queryFn: () => api.get<AnnouncementsResponse>('/announcements'),
    refetchInterval: 60_000,
  });

  if (query.isError) {
    return <QueryError error={query.error} onRetry={() => void query.refetch()} />;
  }

  const incidents = query.data?.incidents ?? [];
  const windows = query.data?.maintenanceWindows ?? [];
  const unannounced = windows.filter((w) => w.announcedAt === null).length;

  const incidentColumns: Array<DataTableColumn<AnnouncementsResponse['incidents'][number]>> = [
    {
      id: 'reference',
      header: '番号',
      width: 'w-32',
      cell: (i) => <code className="font-mono text-xs">{i.reference}</code>,
    },
    {
      id: 'severity',
      header: '重大度',
      width: 'w-32',
      cell: (i) => (
        <StatusBadge
          status={i.severity}
          size="sm"
          dot
          tone={toneFrom(INCIDENT_SEVERITY_TONE, i.severity)}
          label={labelFrom(INCIDENT_SEVERITY_LABEL, i.severity)}
        />
      ),
    },
    { id: 'title', header: '件名', cell: (i) => <span className="font-medium">{i.title}</span> },
    {
      id: 'status',
      header: '状態',
      width: 'w-28',
      cell: (i) => labelFrom(INCIDENT_STATUS_LABEL, i.status),
    },
    { id: 'startedAt', header: '発生', hideBelow: 'md', cell: (i) => formatRelative(i.startedAt) },
    {
      id: 'actions',
      header: <span className="sr-only">操作</span>,
      align: 'right',
      width: 'w-36',
      cell: (i) => (
        <Button asChild variant="outline" size="sm">
          {/* 配信（「契約者へ告知する」）は /incidents 側にある。 */}
          <Link to="/incidents" search={{ incidentId: i.id }}>
            <Siren aria-hidden />
            告知へ
          </Link>
        </Button>
      ),
    },
  ];

  const windowColumns: Array<DataTableColumn<AnnouncementsResponse['maintenanceWindows'][number]>> = [
    { id: 'title', header: '件名', cell: (w) => <span className="font-medium">{w.title}</span> },
    {
      id: 'kind',
      header: '種別',
      width: 'w-40',
      cell: (w) => (
        <StatusBadge
          status={w.kind}
          size="sm"
          tone={toneFrom(MAINTENANCE_KIND_TONE, w.kind)}
          label={labelFrom(MAINTENANCE_KIND_LABEL, w.kind)}
        />
      ),
    },
    {
      id: 'range',
      header: '実施予定',
      cell: (w) => (
        <span className="text-xs">
          {formatDateTime(w.startsAt)} 〜 {formatDateTime(w.endsAt)}
        </span>
      ),
    },
    {
      id: 'scope',
      header: '対象',
      hideBelow: 'md',
      cell: (w) => (w.serverNodeId ? <code className="font-mono text-xs">{w.serverNodeId}</code> : 'サービス全体'),
    },
    {
      id: 'announced',
      header: '告知',
      width: 'w-28',
      cell: (w) =>
        w.announcedAt ? (
          <Badge variant="success" size="sm">
            済
          </Badge>
        ) : (
          <Badge variant="warning" size="sm">
            未告知
          </Badge>
        ),
    },
    {
      id: 'sla',
      header: 'SLA 除外',
      width: 'w-28',
      hideBelow: 'lg',
      cell: (w) =>
        w.excludeFromSla ? (
          <Badge variant="muted" size="sm">
            除外する
          </Badge>
        ) : (
          <Badge variant="warning" size="sm">
            除外しない
          </Badge>
        ),
    },
  ];

  return (
    <div className="space-y-6">
      {unannounced > 0 ? (
        <Alert variant="warning">
          <AlertTitle>未告知の予定が {unannounced} 件あります</AlertTitle>
          <AlertDescription>
            告知しないまま実施した計画メンテナンスは、稼働率の算定から除外できません。
            「メンテナンス予定」タブから告知してください。
          </AlertDescription>
        </Alert>
      ) : null}

      <Section
        title="進行中の障害（公開文面あり）"
        description="公開ステータスと契約者への告知に使う文面が入っているものだけを出しています。"
        actions={<RefetchingDot active={query.isFetching && !query.isLoading} />}
      >
        <DataTable
          columns={incidentColumns}
          rows={incidents}
          rowKey={(i) => i.id}
          loading={query.isLoading}
          empty={<ListEmpty filtered={false} entityLabel="告知中の障害" />}
        />
      </Section>

      <Section title="30 日以内のメンテナンス予定" description="取り消し済みと終了済みは出しません。">
        <DataTable
          columns={windowColumns}
          rows={windows}
          rowKey={(w) => w.id}
          loading={query.isLoading}
          empty={<ListEmpty filtered={false} entityLabel="メンテナンス予定" />}
        />
      </Section>
    </div>
  );
}

/* ================================================== メンテナンス予定 */

function WindowsTab(): React.ReactElement {
  const canWrite = usePermission(OPS_PERMISSION.billingWrite);
  const [createOpen, setCreateOpen] = React.useState(false);
  const [canceling, setCanceling] = React.useState<MaintenanceWindowEntry | null>(null);

  const query = useQuery({
    queryKey: qk.maintenanceWindows(),
    queryFn: () => api.get<MaintenanceWindowPage>('/maintenance-windows', { limit: 100 }),
  });

  const rows = query.data?.items ?? [];
  const noticeDays = query.data?.noticeDays ?? 7;
  const monthlyMinutesMax = query.data?.monthlyMinutesMax ?? 240;

  const columns: Array<DataTableColumn<MaintenanceWindowEntry>> = [
    {
      id: 'title',
      header: '件名',
      cell: (w) => (
        <div className="min-w-0">
          <span className={w.canceledAt ? 'block truncate text-muted-foreground line-through' : 'block truncate font-medium'}>
            {w.title}
          </span>
          {w.bodyMd ? <span className="block truncate text-xs text-muted-foreground">{w.bodyMd}</span> : null}
        </div>
      ),
    },
    {
      id: 'kind',
      header: '種別',
      width: 'w-40',
      cell: (w) => (
        <StatusBadge
          status={w.kind}
          size="sm"
          tone={toneFrom(MAINTENANCE_KIND_TONE, w.kind)}
          label={labelFrom(MAINTENANCE_KIND_LABEL, w.kind)}
        />
      ),
    },
    {
      id: 'range',
      header: '実施',
      cell: (w) => (
        <span className="text-xs">
          {formatDateTime(w.startsAt)} 〜 {formatDateTime(w.endsAt)}（{w.minutes} 分）
        </span>
      ),
    },
    {
      id: 'scope',
      header: '対象',
      hideBelow: 'lg',
      cell: (w) => (w.serverNodeId ? <code className="font-mono text-xs">{w.serverNodeId}</code> : 'サービス全体'),
    },
    {
      id: 'state',
      header: '状態',
      width: 'w-40',
      cell: (w) =>
        w.canceledAt ? (
          <Badge variant="muted" size="sm">
            取り消し済み
          </Badge>
        ) : (
          <div className="flex flex-wrap gap-1">
            {w.announcedAt ? (
              <Badge variant="success" size="sm">
                告知済み
              </Badge>
            ) : (
              <Badge variant="warning" size="sm">
                未告知
              </Badge>
            )}
            {/* 「登録できたが除外にはならない」を見せないと、免責の前提を運営が誤解する。 */}
            {w.excludeFromSla ? null : (
              <Badge variant="warning" size="sm">
                SLA 除外なし
              </Badge>
            )}
          </div>
        ),
    },
    {
      id: 'actions',
      header: <span className="sr-only">操作</span>,
      align: 'right',
      width: 'w-56',
      cell: (w) => (canWrite ? <WindowActions window={w} onCancel={() => setCanceling(w)} /> : null),
    },
  ];

  return (
    <>
      <Alert className="mb-4">
        <AlertDescription>
          計画メンテナンスを稼働率の算定から除外できるのは、<strong>{noticeDays} 日以上前に告知したもの</strong>
          だけです（掲示の条件）。同じ月の除外対象の合計が {Math.floor(monthlyMinutesMax / 60)} 時間を超えた分も除外されません。
        </AlertDescription>
      </Alert>

      <div className="mb-4 flex items-center gap-2">
        <RefetchingDot active={query.isFetching && !query.isLoading} />
        {canWrite ? (
          <Button size="sm" className="ml-auto" onClick={() => setCreateOpen(true)}>
            <Plus aria-hidden />
            メンテナンスを登録
          </Button>
        ) : null}
      </div>

      {query.isError ? (
        <QueryError error={query.error} onRetry={() => void query.refetch()} className="mb-4" />
      ) : null}

      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(w) => w.id}
        loading={query.isLoading}
        empty={<ListEmpty filtered={false} entityLabel="メンテナンス予定" />}
      />

      <CreateWindowDialog open={createOpen} onOpenChange={setCreateOpen} noticeDays={noticeDays} />
      <CancelWindowDialog window={canceling} onClose={() => setCanceling(null)} />
    </>
  );
}

/* ---------------------------------------------------------- 行アクション */

function WindowActions({
  window: w,
  onCancel,
}: {
  window: MaintenanceWindowEntry;
  onCancel: () => void;
}): React.ReactElement | null {
  const qc = useQueryClient();
  const [announceOpen, setAnnounceOpen] = React.useState(false);

  if (w.canceledAt) return null;
  const finished = new Date(w.endsAt).getTime() < Date.now();

  return (
    <div className="flex justify-end gap-1">
      {/* 終了済み・取り消し済みはサーバーが 400 で断る。押せないようにしておく。 */}
      {!finished ? (
        <Button variant="outline" size="sm" onClick={() => setAnnounceOpen(true)}>
          <Megaphone aria-hidden />
          {w.announcedAt ? '再告知' : '告知する'}
        </Button>
      ) : null}
      <Button variant="ghost" size="sm" onClick={onCancel}>
        取り消し
      </Button>

      <AnnounceDialog
        window={w}
        open={announceOpen}
        onOpenChange={setAnnounceOpen}
        onDone={() => {
          void qc.invalidateQueries({ queryKey: ['ops', 'maintenance-windows'] });
          void qc.invalidateQueries({ queryKey: qk.announcements() });
        }}
      />
    </div>
  );
}

function AnnounceDialog({
  window: w,
  open,
  onOpenChange,
  onDone,
}: {
  window: MaintenanceWindowEntry;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDone: () => void;
}): React.ReactElement {
  const [email, setEmail] = React.useState(true);
  const [result, setResult] = React.useState<{ notified: number; failed: number } | null>(null);

  React.useEffect(() => {
    if (open) setResult(null);
  }, [open]);

  const submit = useMutation({
    mutationFn: () =>
      api.post<{ ok: boolean; notified: number; failed: number; announcedAt: string }>(
        `/maintenance-windows/${w.id}/announce`,
        { email },
      ),
    onSuccess: (res) => {
      setResult({ notified: res.notified, failed: res.failed });
      onDone();
    },
  });

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title="計画メンテナンスを告知する"
      description={`「${w.title}」を対象の契約者へ配信します。${
        w.serverNodeId ? '対象ノードに乗っている組織' : 'すべての契約組織'
      }が宛先になります。`}
      submitLabel={result ? '閉じる' : '告知する'}
      pending={submit.isPending}
      error={submit.error}
      onSubmit={() => (result ? onOpenChange(false) : submit.mutate())}
    >
      {result ? (
        <Alert variant={result.failed > 0 ? 'warning' : 'default'}>
          <AlertDescription>
            {result.notified} 組織へ配信しました
            {result.failed > 0 ? `（${result.failed} 組織は失敗。再告知で送り直せます）` : ''}。
          </AlertDescription>
        </Alert>
      ) : (
        <>
          <label className="flex items-start gap-2 rounded-md border border-border bg-panel px-3 py-2.5 text-sm">
            <input
              type="checkbox"
              checked={email}
              onChange={(e) => setEmail(e.target.checked)}
              className="mt-0.5 size-4 rounded border-border"
            />
            <span>
              メールでも送る
              <span className="mt-0.5 block text-xs text-muted-foreground">
                外すとコントロールパネルのお知らせだけになります。
              </span>
            </span>
          </label>
          {w.announcedAt ? (
            <Alert>
              <AlertDescription>
                {formatDateTime(w.announcedAt)} に告知済みです。同じ予定への通知は重複しないよう束ねられます。
              </AlertDescription>
            </Alert>
          ) : null}
        </>
      )}
    </FormDialog>
  );
}

function CancelWindowDialog({
  window: w,
  onClose,
}: {
  window: MaintenanceWindowEntry | null;
  onClose: () => void;
}): React.ReactElement {
  const qc = useQueryClient();
  const submit = useMutation({
    mutationFn: () => api.post(`/maintenance-windows/${w?.id ?? ''}/cancel`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['ops', 'maintenance-windows'] });
      void qc.invalidateQueries({ queryKey: qk.announcements() });
      onClose();
    },
  });

  return (
    <ConfirmTextDialog
      open={w !== null}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title="メンテナンス予定を取り消す"
      description="告知済みの場合、契約者には「取り消し」の通知は自動では飛びません。必要なら別途連絡してください。"
      confirmText={w?.title ?? ''}
      confirmLabel="取り消す"
      pending={submit.isPending}
      error={submit.error}
      onConfirm={() => submit.mutate()}
    />
  );
}

/* -------------------------------------------------------------- 登録 */

function CreateWindowDialog({
  open,
  onOpenChange,
  noticeDays,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  noticeDays: number;
}): React.ReactElement {
  const qc = useQueryClient();
  const [kind, setKind] = React.useState('planned');
  const [serverNodeId, setServerNodeId] = React.useState('');
  const [startsAt, setStartsAt] = React.useState('');
  const [endsAt, setEndsAt] = React.useState('');
  const [title, setTitle] = React.useState('');
  const [bodyMd, setBodyMd] = React.useState('');
  const [announced, setAnnounced] = React.useState(false);
  const [warnings, setWarnings] = React.useState<string[]>([]);

  const nodes = useQuery({
    queryKey: qk.nodes({ scope: 'maintenance' }),
    queryFn: () => api.get<{ items: NodeSummary[] }>('/nodes', { limit: 200 }),
    enabled: open,
  });

  React.useEffect(() => {
    if (!open) return;
    setKind('planned');
    setServerNodeId('');
    setStartsAt('');
    setEndsAt('');
    setTitle('');
    setBodyMd('');
    setAnnounced(false);
    setWarnings([]);
  }, [open]);

  const submit = useMutation({
    mutationFn: () =>
      api.post<{ id: string; warnings?: string[] }>('/maintenance-windows', {
        ...(serverNodeId ? { serverNodeId } : {}),
        kind,
        // `datetime-local` はタイムゾーンを持たない。ブラウザのローカル時刻として
        // 解釈してから ISO（UTC）へ直す。サーバーは `z.string().datetime()`。
        startsAt: new Date(startsAt).toISOString(),
        endsAt: new Date(endsAt).toISOString(),
        title: title.trim(),
        ...(bodyMd.trim().length > 0 ? { bodyMd: bodyMd.trim() } : {}),
        announced,
      }),
    onSuccess: (res) => {
      void qc.invalidateQueries({ queryKey: ['ops', 'maintenance-windows'] });
      void qc.invalidateQueries({ queryKey: qk.announcements() });
      // 警告（告知が間に合っていない・月間上限の超過）は閉じずに見せる。
      if (res.warnings && res.warnings.length > 0) setWarnings(res.warnings);
      else onOpenChange(false);
    },
  });

  const startMs = startsAt ? new Date(startsAt).getTime() : NaN;
  const endMs = endsAt ? new Date(endsAt).getTime() : NaN;
  const invalid =
    title.trim().length === 0 || Number.isNaN(startMs) || Number.isNaN(endMs) || endMs <= startMs;

  // 掲示の条件を満たすか（サーバーと同じ判定）。満たさないと SLA 除外にならない。
  const noticeOk = announced && !Number.isNaN(startMs) && startMs - Date.now() >= noticeDays * 86_400_000;

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title="メンテナンス・停止を登録する"
      description="計画メンテナンスのほか、不可抗力・契約者起因・AUP 停止の記録にも使います。"
      submitLabel={warnings.length > 0 ? '閉じる' : '登録する'}
      pending={submit.isPending}
      error={submit.error}
      disabled={warnings.length === 0 && invalid}
      onSubmit={() => (warnings.length > 0 ? onOpenChange(false) : submit.mutate())}
      wide
    >
      {warnings.length > 0 ? (
        <Alert variant="warning">
          <AlertTitle>登録しましたが、注意があります</AlertTitle>
          <AlertDescription>
            <ul className="list-disc space-y-1 pl-4">
              {warnings.map((w) => (
                <li key={w}>{w}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3">
            <Field label="種別" required>
              <NativeSelect value={kind} onChange={setKind} options={optionsFromLabels(MAINTENANCE_KIND_LABEL)} />
            </Field>
            <Field label="対象ノード" hint="未選択ならサービス全体（全契約者が対象）。">
              <NativeSelect
                value={serverNodeId}
                onChange={setServerNodeId}
                options={[
                  { value: '', label: 'サービス全体' },
                  ...(nodes.data?.items ?? []).map((n) => ({ value: n.id, label: n.hostname })),
                ]}
              />
            </Field>
          </div>

          <Field label="件名" required>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="開始日時" required>
              <Input type="datetime-local" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} />
            </Field>
            <Field label="終了日時" required>
              <Input type="datetime-local" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} />
            </Field>
          </div>

          <Field label="告知文" hint="空にすると「送受信が断続的にご利用いただけない場合があります」の定型文になります。">
            <Textarea value={bodyMd} onChange={(e) => setBodyMd(e.target.value)} rows={4} />
          </Field>

          <label className="flex items-start gap-2 rounded-md border border-border bg-panel px-3 py-2.5 text-sm">
            <input
              type="checkbox"
              checked={announced}
              onChange={(e) => setAnnounced(e.target.checked)}
              className="mt-0.5 size-4 rounded border-border"
            />
            <span>
              告知済みとして記録する
              <span className="mt-0.5 block text-xs text-muted-foreground">
                実際の配信は登録後に「告知する」を押します。ここは、既に別経路で周知済みのときの記録用です。
              </span>
            </span>
          </label>

          {kind === 'planned' && !noticeOk ? (
            <Alert variant="warning">
              <AlertDescription>
                この内容では稼働率の算定から<strong>除外されません</strong>。掲示の条件は「{noticeDays}
                日以上前に告知した計画メンテナンス」です。
              </AlertDescription>
            </Alert>
          ) : null}
        </>
      )}
    </FormDialog>
  );
}
